import { useEffect } from "react";
import { scrollElementIntoScroller } from "../utils";

const KEYBOARD_MIN_OVERLAP = 120;
const KEYBOARD_INSET_PROPERTY = "--keyboard-inset";
const FIELD_WRAPPER_SELECTOR = "[class*='FormRow-module']";

const isTextEntryTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return (
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
  );
};

const useKeyboardInset = () => {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    let focusedElement: HTMLElement | null = null;
    let frame = 0;

    const getInset = () => {
      const overlap = Math.max(0, window.innerHeight - viewport.height);
      return overlap > KEYBOARD_MIN_OVERLAP ? overlap : 0;
    };

    const writeInset = (inset: number) =>
      document.documentElement.style.setProperty(
        KEYBOARD_INSET_PROPERTY,
        `${inset}px`,
      );

    const scrollFocusedIntoView = () => {
      if (!focusedElement) return;
      const target =
        focusedElement.closest<HTMLElement>(FIELD_WRAPPER_SELECTOR) ??
        focusedElement;

      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => scrollElementIntoScroller(target));
    };

    const handleViewportResize = () => {
      const inset = getInset();
      writeInset(inset);
      if (inset) scrollFocusedIntoView();
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!isTextEntryTarget(event.target)) return;
      focusedElement = event.target as HTMLElement;
      if (window.matchMedia("(pointer: coarse)").matches) scrollFocusedIntoView();
    };

    const handleFocusOut = () => {
      focusedElement = null;
    };

    viewport.addEventListener("resize", handleViewportResize);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    writeInset(getInset());

    return () => {
      viewport.removeEventListener("resize", handleViewportResize);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
      cancelAnimationFrame(frame);
      document.documentElement.style.removeProperty(KEYBOARD_INSET_PROPERTY);
    };
  }, []);
};

export default useKeyboardInset;
